export interface MeasureTarget {
  id: string;
  label: string;
  value: number;
  unit: "%" | "hrs";
}

export interface FacilityConfig {
  id: string;
  name: string;
  selected: boolean;
  targets: Record<string, number>;
}

export interface RiskLevelThreshold {
  level: "high" | "medium" | "low";
  label: string;
  fieldLabel: string;
  value: number;
  description: string;
}

export interface RiskFactorPointConfig {
  id: string;
  label: string;
  description: string;
  points: number;
  category: "primary" | "new-insights";
}

export interface ConfigurationState {
  enterpriseTargets: MeasureTarget[];
  facilities: FacilityConfig[];
  riskLevelThresholds: RiskLevelThreshold[];
  riskFactorPoints: RiskFactorPointConfig[];
  disabledRiskFactors: string[];
}

// =============================================================================
// API Types (snake_case from backend)
// =============================================================================

/** Shape returned by GET /api/admin/care-queue/config */
export interface CareQueueConfigApiResponse {
  config: {
    client_id: string;
    enterprise_targets: Array<{
      id: string;
      label: string;
      value: number;
      unit: "%" | "hrs";
    }>;
    facilities: Array<{
      id: string;
      name: string;
      selected: boolean;
      targets: Record<string, number>;
    }>;
    risk_level_thresholds: Array<{
      level: "high" | "medium" | "low";
      label: string;
      field_label: string;
      value: number;
      description: string;
    }>;
    risk_factor_points: Array<{
      id: string;
      label: string;
      description: string;
      points: number;
      category: "primary" | "new-insights";
    }>;
    created_at: string;
    updated_at: string;
    created_by: string | null;
    updated_by: string | null;
  };
  message: string;
}

/** Shape sent to PUT /api/admin/care-queue/config */
export interface SaveCareQueueConfigApiRequest {
  enterprise_targets: Array<{
    id: string;
    label: string;
    value: number;
    unit: "%" | "hrs";
  }>;
  facilities: Array<{
    id: string;
    name: string;
    selected: boolean;
    targets: Record<string, number>;
  }>;
  risk_level_thresholds: Array<{
    level: "high" | "medium" | "low";
    label: string;
    field_label: string;
    value: number;
    description: string;
  }>;
  risk_factor_points: Array<{
    id: string;
    label: string;
    description: string;
    points: number;
    category: "primary" | "new-insights";
  }>;
}

/** Per-facility entry in the risk-config response */
export interface FacilityRiskConfigEntry {
  client_id: string;
  facility_id: string;
  custom_config?: {
    disabled_risk_factors?: string[];
  } | null;
}

/** Shape returned by GET /api/facility-settings/risk-config.
 *  Dict keyed by facility_id. */
export type FacilityRiskConfigApiResponse = Record<string, FacilityRiskConfigEntry>;

// =============================================================================
// Mapping Functions
// =============================================================================

/** Convert API response (snake_case) → frontend ConfigurationState (camelCase) */
export function mapApiResponseToConfig(
  apiConfig: CareQueueConfigApiResponse["config"],
): ConfigurationState {
  return {
    enterpriseTargets: apiConfig.enterprise_targets,
    facilities: apiConfig.facilities,
    riskLevelThresholds: apiConfig.risk_level_thresholds.map((t) => ({
      level: t.level,
      label: t.label,
      fieldLabel: t.field_label,
      value: t.value,
      description: t.description,
    })),
    riskFactorPoints: apiConfig.risk_factor_points,
    disabledRiskFactors: [], // populated separately via risk config API
  };
}

/** Convert frontend ConfigurationState (camelCase) → API request (snake_case) */
export function mapConfigToApiRequest(
  config: ConfigurationState,
): SaveCareQueueConfigApiRequest {
  return {
    enterprise_targets: config.enterpriseTargets,
    facilities: config.facilities,
    risk_level_thresholds: config.riskLevelThresholds.map((t) => ({
      level: t.level,
      label: t.label,
      field_label: t.fieldLabel,
      value: t.value,
      description: t.description,
    })),
    risk_factor_points: config.riskFactorPoints,
  };
}
